import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import Navbar from './Navbar';
import './RequestViewer.css';
import { FaSearch, FaEye, FaCheck, FaTimes, FaTrash } from 'react-icons/fa';
import SpecificDaysModal from './SpecificDaysModal';
import RequestActionModal from './RequestActionModal';
import { checkAndUpdateEmployeeData } from './employeeUpdates';

function RequestViewer() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [isPrevDisabled, setIsPrevDisabled] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [userRole, setUserRole] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionRequest, setActionRequest] = useState(null);
  const [actionMode, setActionMode] = useState(null); // 'accept' or 'decline'
  const [isDeleting, setIsDeleting] = useState(false);
  
  const ROWS_PER_PAGE = 10;
  const isLoadingRef = useRef(false);
  const searchTimeoutRef = useRef(null);

  // Get user info (role and email)
  const getUserInfo = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data, error } = await supabase
          .from('users')
          .select('role, wsp_email')
          .eq('wsp_email', user.email)
          .single();

        if (error) throw error;
        setUserRole(data.role);
        setUserEmail(data.wsp_email);
      }
    } catch (error) {
      console.error('Error fetching user info:', error.message);
    }
  };

  useEffect(() => {
    getUserInfo();
  }, []);

  // Fetch vacation requests with role-based filtering
  const fetchRequests = useCallback(async (page = 0, search = '') => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;

    setIsPageLoading(true);
    setLoading(true);

    try {
      const from = page * ROWS_PER_PAGE;
      const to = from + ROWS_PER_PAGE - 1;

      // Build query based on user role
      let query = supabase.from('vacation_requests').select('*', { count: 'exact' });
      let queryFilters = [];
      
      // Apply role-based filtering
      if (userRole === 'admin') {
        // Admin can see all requests, no filters needed
      } else if (userRole === 'RH') {
        // RH can see all requests (pending and accepted) from all areas.
        // Button restrictions will handle what actions they can perform.
        // No specific query filters needed here for fetching, similar to admin.
      } else if (userRole === 'CEO') {
        // CEO can see requests from specific areas
        const { data: areaEmployees, error: areaError } = await supabase
          .from('employees')
          .select('id')
          .in('area', ['Administrativo / Dirección', 'Comercial', 'Ventas']);
          
        if (areaError) throw areaError;
        
        const areaEmployeeIds = areaEmployees.map(emp => emp.id);
        query = query.in('employee_id', areaEmployeeIds);
      } else {
        // For other roles (Fábrica ABAP, Staffing, PMO-IT, Finanzas)
        // They can see:
        // 1. Requests from employees in their area
        // 2. Their own requests
        
        // First, get employees from the same area
        const { data: areaEmployees, error: areaError } = await supabase
          .from('employees')
          .select('id')
          .eq('area', userRole);
          
        if (areaError) throw areaError;
        
        const areaEmployeeIds = areaEmployees.map(emp => emp.id);
        
        // Then, get the current user's employee ID
        const { data: currentEmployee, error: userError } = await supabase
          .from('employees')
          .select('id')
          .eq('wsp_email', userEmail)
          .single();
          
        if (userError && userError.code !== 'PGRST116') {
          // PGRST116 means no rows returned, which might happen if user doesn't have an employee record
          throw userError;
        }
        
        // Combine the filters
        if (currentEmployee) {
          query = query.or(`employee_id.in.(${areaEmployeeIds.join(',')}),employee_id.eq.${currentEmployee.id}`);
        } else if (areaEmployeeIds.length > 0) {
          query = query.in('employee_id', areaEmployeeIds);
        } else {
          // If no matches, return empty results
          setRequests([]);
          setTotalCount(0);
          setIsNextDisabled(true);
          setIsPrevDisabled(page === 0);
          setHasMore(false);
          setLoading(false);
          setIsPageLoading(false);
          isLoadingRef.current = false;
          return;
        }
      }

      // Add search filter if provided
      if (search) {
        // We need to join with employees to search by name
        const { data: employees, error: employeesError } = await supabase
          .from('employees')
          .select('id')
          .ilike('full_name', `%${search}%`);

        if (employeesError) throw employeesError;
        
        if (employees && employees.length > 0) {
          const employeeIds = employees.map(e => e.id);
          query = query.in('employee_id', employeeIds);
        } else if (search) {
          // If no employees match the search, return empty
          setRequests([]);
          setTotalCount(0);
          setIsNextDisabled(true);
          setIsPrevDisabled(page === 0);
          setHasMore(false);
          setLoading(false);
          setIsPageLoading(false);
          isLoadingRef.current = false;
          return;
        }
      }

      const { count, error: countError } = await query
        .range(0, 0)
        .limit(1);

      if (countError) throw countError;
      setTotalCount(count || 0);

      if (count === 0 || from >= count) {
        setRequests([]);
        setIsNextDisabled(true);
        setIsPrevDisabled(page === 0);
        setHasMore(false);
        setLoading(false);
        setIsPageLoading(false);
        isLoadingRef.current = false;
        return;
      }

      // Fetch the vacation requests
      const { data: fetchedRequests, error } = await query
        .range(from, to)
        .order('request_date', { ascending: false });

      if (error) throw error;

      // Check and delete old requests
      const currentDate = new Date();
      const requestsToDelete = [];
      
      // Identify requests that need to be deleted
      fetchedRequests.forEach(request => {
        if (request.status === true && request.accepted_date) {
          // For accepted requests, check if accepted_date is 3+ months old
          const acceptedDate = new Date(request.accepted_date);
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          
          if (acceptedDate <= threeMonthsAgo) {
            requestsToDelete.push(request.id);
          }
        } else if (request.status === false && request.request_date) {
          // For pending requests, check if request_date is 6+ months old
          const requestDate = new Date(request.request_date);
          const sixMonthsAgo = new Date();
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
          
          if (requestDate <= sixMonthsAgo) {
            requestsToDelete.push(request.id);
          }
        }
      });
      
      // Delete old requests if any found
      if (requestsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('vacation_requests')
          .delete()
          .in('id', requestsToDelete);
          
        if (deleteError) throw deleteError;
        
        console.log(`Deleted ${requestsToDelete.length} old vacation requests`);
        
        // Remove deleted requests from our fetched results
        const remainingRequests = fetchedRequests.filter(
          request => !requestsToDelete.includes(request.id)
        );
        
        // If we deleted all fetched requests, refetch the current page
        if (remainingRequests.length === 0 && count > ROWS_PER_PAGE) {
          return fetchRequests(page, search);
        }
        
        // Update fetched requests to only include remaining ones
        fetchedRequests.length = 0;
        fetchedRequests.push(...remainingRequests);
      }

      // Get unique employee IDs to fetch only once per employee
      const uniqueEmployeeIds = [...new Set(fetchedRequests.map(req => req.employee_id))];
      
      // Fetch all these employees at once
      const { data: employees, error: employeesError } = await supabase
        .from('employees')
        .select('*')
        .in('id', uniqueEmployeeIds);
        
      if (employeesError) throw employeesError;
      
      // Run the employees through the update algorithm
      const updateResult = await checkAndUpdateEmployeeData(employees);
      
      if (!updateResult.success) {
        console.error('Updates failed:', updateResult.errors);
      }
      
      // Create a map for quick lookup of updated employee data
      const employeeMap = {};
      updateResult.updatedEmployees.forEach(emp => {
        employeeMap[emp.id] = emp;
      });

      // Now we need to get additional information
      const enrichedRequests = await Promise.all(
        fetchedRequests.map(async (request) => {
          // Get the updated employee from the map
          const employee = employeeMap[request.employee_id];
          
          if (!employee) {
            throw new Error(`Employee not found for ID: ${request.employee_id}`);
          }

          // Count requested days
          const { data: requestDays, error: requestDaysError, count: daysCount } = await supabase
            .from('vacation_requests_days')
            .select('*', { count: 'exact' })
            .eq('request_id', request.id);

          if (requestDaysError) throw requestDaysError;

          return {
            ...request,
            employee_name: employee.full_name,
            employee_area: employee.area,
            employee_email: employee.wsp_email,
            remaining_days: employee.remaining_days,
            requested_days: request.is_half_day ? 0.5 : (daysCount || 0)
          };
        })
      );

      setRequests(enrichedRequests);
      setIsNextDisabled(from + enrichedRequests.length >= count);
      setIsPrevDisabled(page === 0);
      setHasMore(from + enrichedRequests.length < count);
      setError(null);
    } catch (error) {
      console.error('Error:', error);
      setError('Error fetching vacation requests: ' + error.message);
    } finally {
      setLoading(false);
      setIsPageLoading(false);
      isLoadingRef.current = false;
    }
  }, [userRole, userEmail]);

  // Helper functions to determine button visibility
  const shouldShowViewButton = (request) => {
    return true; // All roles can view requests they have access to
  };

  const shouldShowAcceptButton = (request) => {
    if (request.status) return false; // Already accepted
    
    // Admin can accept any request
    if (userRole === 'admin') return true;
    
    // RH can accept pending requests from specific areas
    if (userRole === 'RH') {
      return !request.status && ['Administrativo / Dirección', 'RH', 'Comercial', 'Ventas'].includes(request.employee_area);
    }
    
    // CEO can accept requests from specific areas
    if (userRole === 'CEO') {
      return ['Administrativo / Dirección', 'Comercial', 'Ventas'].includes(request.employee_area);
    }
    
    // Other managers can accept requests from their area but not their own
    if (['Fábrica ABAP', 'Staffing', 'PMO-IT', 'Finanzas'].includes(userRole)) {
      return request.employee_area === userRole && request.employee_email !== userEmail;
    }
    
    return false;
  };

  const shouldShowDeclineButton = (request) => {
    // Same rules as accept button
    return shouldShowAcceptButton(request);
  };

  const shouldShowDeleteButton = (request) => {
    // Admin can delete any request
    if (userRole === 'admin') return true;
    
    // RH can delete:
    // 1. Accepted requests from any area.
    // 2. Pending requests from specific areas.
    if (userRole === 'RH') {
      if (request.status === true) return true; // Accepted requests from any area
      // For pending requests, only allow deletion if from specific areas
      return !request.status && ['Administrativo / Dirección', 'RH', 'Comercial', 'Ventas'].includes(request.employee_area);
    }
    
    // Users can delete their own requests
    if (request.employee_email === userEmail) return true;
    
    return false;
  };

  // Search and initial load effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      fetchRequests(0, searchTerm);
    }, 500); // 500ms delay for search

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, fetchRequests]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    // Add one day to compensate for timezone
    date.setDate(date.getDate() + 1);
    return date.toLocaleDateString('es-MX');
  };

  // Handle search change
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  }, []);

  // Pagination handlers
  const handlePreviousPage = useCallback(() => {
    if (currentPage > 0 && !isPrevDisabled && !isPageLoading && !isLoadingRef.current) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      fetchRequests(newPage, searchTerm);
    }
  }, [currentPage, isPrevDisabled, isPageLoading, searchTerm, fetchRequests]);

  const handleNextPage = useCallback(() => {
    if (hasMore && !isNextDisabled && !isPageLoading && !isLoadingRef.current) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      fetchRequests(newPage, searchTerm);
    }
  }, [currentPage, hasMore, isNextDisabled, isPageLoading, searchTerm, fetchRequests]);

  // Handle view request details
  const handleViewRequest = (request) => {
    setSelectedRequest(request);
  };

  // Handle accept request
  const handleAcceptRequest = (request) => {
    setActionRequest(request);
    setActionMode('accept');
  };

  // Handle decline request
  const handleDeclineRequest = (request) => {
    setActionRequest(request);
    setActionMode('decline');
  };

  // Close the detail modal
  const handleCloseDetailModal = () => {
    setSelectedRequest(null);
  };

  // Close the action modal
  const handleCloseActionModal = () => {
    setActionRequest(null);
    setActionMode(null);
  };

  // Handle delete request
  const handleDeleteRequest = async (request) => {
    if (window.confirm('¿Estás seguro de borrar la solicitud?')) {
      setIsDeleting(true);
      try {
        // Delete the vacation request
        const { error } = await supabase
          .from('vacation_requests')
          .delete()
          .eq('id', request.id);
          
        if (error) throw error;
        
        // Refresh the data
        fetchRequests(currentPage, searchTerm);
      } catch (error) {
        console.error('Error deleting request:', error.message);
        setError('Error al eliminar la solicitud: ' + error.message);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  // Handle successful action
  const handleActionSuccess = () => {
    // Refresh the data
    fetchRequests(currentPage, searchTerm);
  };

  return (
    <div className="request-viewer">
      <Navbar />
      <div className="request-viewer-content">
        <div className="request-viewer-header">
          <div className="search-container">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="search"
                placeholder="Buscar solicitud por nombre de empleado"
                className="search-input"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>
          <h2 className="view-title">Solicitudes de Vacaciones</h2>
        </div>

        <div className="table-container">
          {error && <div className="error-message">{error}</div>}
          {loading ? (
            <div className="loading-message">Cargando solicitudes...</div>
          ) : (
            <table className="requests-table">
              <thead>
                <tr>
                  <th>Nombre Completo</th>
                  <th>No. de días solicitados</th>
                  <th>No. días disponibles</th>
                  <th>Tipo de solicitud</th>
                  <th>Creación de solicitud</th>
                  <th>Estatus</th>
                  <th>Fecha de aceptación</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="no-results">No hay solicitudes para mostrar</td>
                  </tr>
                ) : (
                  requests.map((request) => (
                    <tr key={request.id}>
                      <td data-label="Nombre Completo">{request.employee_name}</td>
                      <td data-label="Días solicitados">{request.requested_days} Días</td>
                      <td data-label="Días disponibles">{request.remaining_days} Días</td>
                      <td data-label="Tipo de solicitud">{request.is_half_day ? 'Medio Día' : 'Día/s completos'}</td>
                      <td data-label="Fecha de creación">{formatDate(request.request_date)}</td>
                      <td data-label="Estatus">
                        <div className="status-container">
                          <span className={request.status ? 'status-accepted' : 'status-pending'}>
                            {request.status ? 'Aceptada' : 'Pendiente'}
                          </span>
                        </div>
                      </td>
                      <td data-label="Fecha de aceptación">{request.accepted_date ? formatDate(request.accepted_date) : 'N/A'}</td>
                      <td data-label="Acciones">
                        <div className="action-buttons">
                          {shouldShowViewButton(request) && (
                            <button
                              className="view-button"
                              title="Ver detalles"
                              onClick={() => handleViewRequest(request)}
                            >
                              <FaEye className="view-icon" />
                            </button>
                          )}
                          
                          {shouldShowAcceptButton(request) && (
                            <button
                              className="accept-button"
                              title="Aceptar solicitud"
                              onClick={() => handleAcceptRequest(request)}
                            >
                              <FaCheck className="accept-icon" />
                            </button>
                          )}
                          
                          {shouldShowDeclineButton(request) && (
                            <button
                              className="decline-button"
                              title="Rechazar solicitud"
                              onClick={() => handleDeclineRequest(request)}
                            >
                              <FaTimes className="decline-icon" />
                            </button>
                          )}
                          
                          {shouldShowDeleteButton(request) && (
                            <button
                              className="delete-button"
                              title="Eliminar solicitud"
                              onClick={() => handleDeleteRequest(request)}
                              disabled={isDeleting}
                            >
                              <FaTrash className="delete-icon" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="pagination">
          <button
            className={`pagination-button ${isPrevDisabled ? 'disabled' : ''}`}
            onClick={handlePreviousPage}
            disabled={isPrevDisabled || isPageLoading}
          >
            Atrás
          </button>
          <span className="page-info">
            Página {currentPage + 1} de {Math.max(1, Math.ceil(totalCount / ROWS_PER_PAGE))}
          </span>
          <button
            className={`pagination-button ${isNextDisabled ? 'disabled' : ''}`}
            onClick={handleNextPage}
            disabled={isNextDisabled || isPageLoading}
          >
            Siguiente
          </button>
        </div>
      </div>

      {/* Specific Days Modal */}
      {selectedRequest && (
        <SpecificDaysModal
          requestId={selectedRequest.id}
          employeeName={selectedRequest.employee_name}
          onClose={handleCloseDetailModal}
        />
      )}

      {/* Request Action Modal */}
      {actionRequest && actionMode && (
        <RequestActionModal
          mode={actionMode}
          request={actionRequest}
          onClose={handleCloseActionModal}
          onSuccess={handleActionSuccess}
        />
      )}
    </div>
  );
}

export default RequestViewer; 